import type { BuilderAccount } from "api-types/common/responseTypes";
import {
  getHomePlanCategoryLabelKey,
  getHomePlanCustomerCustomFields,
  getHomePlanCustomerName,
  getHomePlanSalesAgentDisplayName,
} from "api-types/homePlans/homePlanFields";
import type { ClientHomePlan, ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { getLocationTypeLabel } from "api-types/locations/locationDisplay";
import type { ClientMasterPlanReportingUnit } from "api-types/masterPlans/masterPlansResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { arrayUnique } from "common/utils/arrays";
import { ensureDefined } from "common/utils/assertOfType";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import { getLotSpecLabel } from "lib/components/builders/communities/utils/lotSpecLabels";
import IconHomePlanCategory from "lib/components/typedIcons/IconHomePlanCategory";
import IconHomePlanSource from "lib/components/typedIcons/IconHomePlanSource";
import { useFetch } from "lib/fetcher";
import { homePlanPath, useAppRouterParams } from "lib/routeHelpers";
import { userCanEditHomePlan } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import type { JSX } from "react";
import { useMemo } from "react";
import Link from "ui/components/Link";
import IconDetached from "ui/icons/IconDetached";
import IconLineArrow from "ui/icons/IconLineArrow";
import IconLocked from "ui/icons/IconLocked";
import IconPhaseConstruction from "ui/icons/IconPhaseConstruction";
import IconPhasePlanPrep from "ui/icons/IconPhasePlanPrep";
import IconPhasePreConstruction from "ui/icons/IconPhasePreConstruction";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column, OrderByArgs, PaginationState } from "ui/tables/DataTable/types";
import { buildCustomFieldColumns } from "./customFieldColumns";
import HomePlanContractList, { hasMultipleHomePlanContracts } from "./HomePlanContractList";
import { LotStatusTag } from "./homePlanDisplayUtils";
import type { HomePlanDropdownModalState } from "./HomePlanDropdown";
import HomePlanDropdown from "./HomePlanDropdown";
import HomePlanMultiSelect from "./HomePlanMultiSelect";
import styles from "./HomePlansTable.module.scss";
import { homePlanPhases } from "./PlanPhases";

type HomePlanTableContext = "COMMUNITY" | "COMMUNITY_PLAN";

interface PlanTableProps {
  homePlans: ClientHomePlanHydratedNoDoc[] | undefined;
  setActiveHomePlan: (homePlan: ClientHomePlanHydratedNoDoc) => void;
  menuModalState: HomePlanDropdownModalState;
  context: HomePlanTableContext;
  onMultiUpdate: (homePlans: ClientHomePlanHydratedNoDoc[]) => void;
  onUpdate: (homePlan: ClientHomePlanHydratedNoDoc | ClientHomePlan) => void;
  onDelete: (id: string) => void;
  highlightedRowId: string | undefined;
  location?: ClientLocationWithLevel;
  reportingUnits?: ClientMasterPlanReportingUnit[];
  totalCount?: number;
  pagination?: PaginationState;
  setPagination?: (pagination: PaginationState) => void;
  orderBy?: OrderByArgs<ClientHomePlanHydratedNoDoc>;
  setOrderBy?: (orderBy: OrderByArgs<ClientHomePlanHydratedNoDoc> | undefined) => void;
}

export default function HomePlansTable({
  homePlans,
  menuModalState,
  setActiveHomePlan,
  context,
  onMultiUpdate,
  onUpdate,
  onDelete,
  highlightedRowId,
  location,
  reportingUnits,
  totalCount,
  pagination,
  setPagination,
  orderBy,
  setOrderBy,
}: PlanTableProps) {
  const { builderId } = useAppRouterParams();

  const { hasPermission, user, builder, enabledFeatures } = useUserContext();
  const multiUnitLabel = getLocationTypeLabel("multiunit", builder?.locationSettings);
  const { data: communitySalesStaff } = useFetch<ClientUserHydrated>(
    `${WEB_API_BASE_PATH}/users/${location?.salesStaffId}`,
    [location?.salesStaffId],
  );
  const { data: builderData } = useFetch<BuilderAccount>(`${WEB_API_BASE_PATH}/builders/${builderId}/account`, [
    builderId,
  ]);

  const customFields = builderData?.builder?.salesConfig?.publicShowroomCTA?.customFields;
  const communityMapConfig = builder?.salesConfig?.communityMapConfig;

  const unitNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const unit of reportingUnits ?? []) {
      map.set(unit.reportingUnitId, unit.name);
    }
    return map;
  }, [reportingUnits]);

  const hasReportingUnits = (reportingUnits?.length ?? 0) > 0;
  const isMultiUnitLocation = location?.locationLevel?.type === "multiunit";
  const homePlanDataSetKey = useMemo(() => (homePlans ?? []).map((homePlan) => homePlan.id).join(","), [homePlans]);

  const columns = useMemo(() => {
    const colDef: Column<ClientHomePlanHydratedNoDoc>[] = [
      ...(hasReportingUnits
        ? [
            {
              header: "Unit",
              id: "unitName",
              accessor: (hp: ClientHomePlanHydratedNoDoc) => {
                const reportingUnitId = hp.lot?.reportingUnitId;
                if (!reportingUnitId) {
                  return null;
                }
                return unitNameMap.get(reportingUnitId) ?? reportingUnitId;
              },
              fallback: true,
              enableSorting: false,
            } as Column<ClientHomePlanHydratedNoDoc>,
          ]
        : []),
      {
        header: "Plan ID",
        field: "planSetId",
        fallback: true,
        icon: ({ row }) => {
          if (row.original?.lockedAt) {
            return <IconLocked variant="portal" size={12} />;
          } else if (row.original?.detachedAt) {
            return <IconDetached size={12} />;
          }
          return undefined;
        },
      },
      {
        header: "Customer",
        id: "customerName",
        accessor: (hp) =>
          hasMultipleHomePlanContracts(enabledFeatures.homePlanContracts, hp.contractCount)
            ? `${hp.contractCount} contracts`
            : getHomePlanCustomerName(hp),
        fallback: true,
        icon: ({ row }) => {
          const source = row.original?.source;
          return <IconHomePlanSource source={source} size={16} />;
        },
        headerTooltip: "Home plan source icons are only populated for home plans created after Jan. 26, 2026",
        tooltip: ({ row }) => {
          const source = row.original?.source;
          if (source === "sales_agent") {
            return "Home plan source: sales agent";
          } else if (source === "web_lead") {
            return "Home plan source: web lead";
          }
          return undefined;
        },
      },
      {
        header: "Lot",
        id: "lotNumber",
        accessor: (homePlan) => getLotNumberDisplay(homePlan?.lot) || null,
        fallback: true,
        icon: ({ row }) => {
          const lotStatus = row.original?.lot?.lotStatus;
          const spec = row.original?.lot?.spec ?? "none";
          return lotStatus ? (
            <LotStatusTag lotStatus={lotStatus} spec={spec} communityMapConfig={communityMapConfig} />
          ) : null;
        },
      },
      {
        header: "Type",
        id: "lotSpec",
        accessor: (homePlan) => {
          if (homePlan?.isMultiUnit) {
            return multiUnitLabel;
          }
          const spec = homePlan?.lot?.spec;
          if (!spec) {
            return "Standard";
          }
          return getLotSpecLabel(spec, communityMapConfig);
        },
        fallback: true,
      },
      {
        header: "Sales Agent",
        id: "salesStaffName",
        accessor: (homePlan) => getHomePlanSalesAgentDisplayName(homePlan, communitySalesStaff),
        fallback: true,
      },
      {
        header: "Category",
        field: "category",
        accessor: (homePlan) => getHomePlanCategoryLabelKey(homePlan),
        icon: ({ row }) => {
          const category = row.original?.category;
          return category ? <IconHomePlanCategory category={category} /> : null;
        },
      },
      ...(!isMultiUnitLocation
        ? [
            {
              header: "Phase",
              field: "planStatus",
              accessor: (homePlan: ClientHomePlanHydratedNoDoc) => homePlanPhases[homePlan?.planStatus]?.name,
              icon: ({ row }: { row: { original: ClientHomePlanHydratedNoDoc } }) => {
                const planStatus = row.original?.planStatus;
                let icon: JSX.Element | undefined;

                switch (planStatus) {
                  case "PREP": {
                    icon = <IconPhasePlanPrep />;
                    break;
                  }
                  case "PRE_CONSTRUCTION": {
                    icon = <IconPhasePreConstruction />;
                    break;
                  }
                  case "CONSTRUCTION": {
                    icon = <IconPhaseConstruction />;
                    break;
                  }
                }

                return icon;
              },
            } as Column<ClientHomePlanHydratedNoDoc>,
          ]
        : []),
      {
        header: "Created",
        field: "createdAt",
        type: "DATE",
      },
      {
        header: "External ID",
        field: "externalId",
        fallback: true,
        hide: true,
      },
      {
        header: "Notes",
        field: "notes",
        fallback: true,
        hide: true,
      },
    ];

    // Custom fields splice position: after Plan ID/Customer/Lot, before Type/Sales Agent
    colDef.splice(
      3,
      0,
      ...buildCustomFieldColumns<ClientHomePlanHydratedNoDoc>(customFields, (row) =>
        getHomePlanCustomerCustomFields(row),
      ),
    );

    const showMasterPlanColumn = context === "COMMUNITY" && !isMultiUnitLocation;
    if (showMasterPlanColumn) {
      colDef.splice(3, 0, {
        header: "Master Plan",
        field: "masterPlanName",
        accessor: (hp) => hp.masterPlanName,
        fallback: true,
      });
    }

    if (homePlans) {
      const uniqueLocations = arrayUnique(homePlans?.map((hp) => hp.communityName).filter(Boolean) ?? []);
      if (uniqueLocations.some((name) => name !== location?.name)) {
        const insertPosition = showMasterPlanColumn ? 4 : 3;
        colDef.splice(insertPosition, 0, {
          header: "Location",
          field: "communityName",
          accessor: (homePlan) => {
            const locationName = homePlan.communityName;
            if (!locationName || locationName === location?.name) {
              return null;
            }
            return (
              <Link
                href={`/builders/${builderId}/locations/${homePlan.communityId}/home-plans`}
                className={styles.locationLink}
              >
                <span>{locationName}</span>
                <IconLineArrow />
              </Link>
            );
          },
        });
      }
    }

    return colDef;
  }, [
    homePlans,
    location?.name,
    builderId,
    builderData,
    customFields,
    communityMapConfig,
    communitySalesStaff,
    hasReportingUnits,
    unitNameMap,
    isMultiUnitLocation,
    multiUnitLabel,
    context,
    enabledFeatures.homePlanContracts,
  ]);

  const hasExpandableContracts = !!homePlans?.some((homePlan) =>
    hasMultipleHomePlanContracts(enabledFeatures.homePlanContracts, homePlan.contractCount),
  );

  return (
    <div className={styles.wrapper}>
      <ConfigTable
        columns={columns}
        showHideColumns
        showHideColumnsStorageKey="home-plans-column-visibility"
        data={homePlans ?? []}
        getRowHref={({ original }) => homePlanPath(ensureDefined(builderId), original.id)}
        isRowHighlighted={({ original }) => original.id === highlightedRowId}
        overflowMenu={(row) => (
          <HomePlanDropdown
            setActiveHomePlan={setActiveHomePlan}
            modalState={menuModalState}
            homePlan={row.original}
            onUpdate={onUpdate}
            onDelete={onDelete}
            location={location}
          />
        )}
        enableRowSelection={(row) => userCanEditHomePlan({ hasPermission, user }, row.original, location)}
        multiSelect={(rows) => (
          <HomePlanMultiSelect
            homePlans={rows
              .filter((row) => userCanEditHomePlan({ hasPermission, user }, row.original, location))
              .map((r) => r.original)}
            onMultiUpdate={onMultiUpdate}
            onDelete={onDelete}
          />
        )}
        totalCount={totalCount}
        pagination={pagination}
        setPagination={setPagination}
        dataSetKey={homePlanDataSetKey}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        getRowCanExpand={
          hasExpandableContracts
            ? ({ original }) => hasMultipleHomePlanContracts(enabledFeatures.homePlanContracts, original.contractCount)
            : undefined
        }
        expandableRow={
          hasExpandableContracts
            ? ({ original }) => (
                <HomePlanContractList
                  builderId={ensureDefined(builderId)}
                  homePlanId={original.id}
                  canEdit={userCanEditHomePlan({ hasPermission, user }, original, location)}
                  variant="table"
                  contractCount={original.contractCount}
                />
              )
            : undefined
        }
      />
    </div>
  );
}
